/**
 * ColumnManagementModal Component
 *
 * Modal per la gestione delle colonne Kanban (crea/modifica/elimina).
 * L'eliminazione richiede di scegliere una colonna di destinazione quando la colonna
 * contiene task; è disabilitata quando è l'unica colonna attiva rimasta. Tutti gli utenti
 * autenticati possono gestire tutte le colonne, incluse quelle seed "Gamma"/"Personalizzati".
 */
import { useEffect, useState } from 'react';
import { Alert, Button, ColorInput, Group, Modal, Select, Stack, Text, TextInput, Textarea } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { AUTO_ASSIGN_CATEGORY } from '../../types/api';
import type { CreateKanbanColumnRequest, KanbanColumnDefinition, UpdateKanbanColumnRequest } from '../../types/api';
import { useKanbanColumns } from '../../hooks/use-kanban-columns';

interface ColumnManagementModalProps {
  opened: boolean;
  onClose: () => void;
  column?: KanbanColumnDefinition | null;
  onSave: (data: CreateKanbanColumnRequest | UpdateKanbanColumnRequest) => void;
  onDelete?: (id: number, moveTasksToColumnId?: number) => void;
  isLoading?: boolean;
  /** Se true, il modal si apre già sulla vista di conferma eliminazione (menu "Elimina colonna"). */
  initialDeleteConfirm?: boolean;
}

const DEFAULT_COLORS = ['#6b7280', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

const AUTO_ASSIGN_OPTIONS = [
  { value: '', label: 'Nessuno' },
  { value: AUTO_ASSIGN_CATEGORY.Gamma, label: 'Gamma' },
  { value: AUTO_ASSIGN_CATEGORY.Personalizzati, label: 'Personalizzati' },
];

export default function ColumnManagementModal({
  opened,
  onClose,
  column,
  onSave,
  onDelete,
  isLoading = false,
  initialDeleteConfirm = false,
}: ColumnManagementModalProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [moveTasksToColumnId, setMoveTasksToColumnId] = useState<string | null>(null);

  const { data: allColumns = [] } = useKanbanColumns();
  const isEditMode = !!column;
  const activeColumns = allColumns.filter((c) => c.isActive);
  const isOnlyActiveColumn = isEditMode && activeColumns.length <= 1;
  const moveTargetOptions = activeColumns
    .filter((c) => c.id !== column?.id)
    .map((c) => ({ value: String(c.id), label: c.name }));

  const form = useForm({
    initialValues: {
      name: '',
      description: '',
      color: '#6b7280',
      autoAssignCategory: '',
    },
    validate: {
      name: (value) => (!value?.trim() ? 'Il nome è obbligatorio' : null),
    },
  });

  useEffect(() => {
    if (column) {
      form.setValues({
        name: column.name,
        description: column.description || '',
        color: column.color,
        autoAssignCategory: column.autoAssignCategory || '',
      });
    } else {
      form.reset();
    }
    setShowDeleteConfirm(initialDeleteConfirm);
    setMoveTasksToColumnId(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [column, opened]);

  const handleSubmit = form.onSubmit((values) => {
    const data = {
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      color: values.color,
      autoAssignCategory: values.autoAssignCategory || null,
    };
    onSave(data);
  });

  const needsMoveTarget = isEditMode && (column?.taskCount ?? 0) > 0;
  const canConfirmDelete = !needsMoveTarget || !!moveTasksToColumnId;

  const handleDelete = () => {
    if (!column || !onDelete) return;
    onDelete(column.id, moveTasksToColumnId ? Number(moveTasksToColumnId) : undefined);
  };

  const handleClose = () => {
    form.reset();
    setShowDeleteConfirm(false);
    setMoveTasksToColumnId(null);
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={isEditMode ? 'Modifica Colonna' : 'Nuova Colonna'} size="md">
      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          <TextInput label="Nome" placeholder="Es. Gamma" required {...form.getInputProps('name')} />

          <Textarea
            label="Descrizione"
            placeholder="Descrizione opzionale della colonna"
            rows={3}
            {...form.getInputProps('description')}
          />

          <ColorInput label="Colore" placeholder="Seleziona un colore" swatches={DEFAULT_COLORS} {...form.getInputProps('color')} />

          <Select
            label="Riceve automaticamente i task di"
            description="I nuovi task delle richieste di questo tipo verranno inseriti qui automaticamente"
            data={AUTO_ASSIGN_OPTIONS}
            {...form.getInputProps('autoAssignCategory')}
          />

          {showDeleteConfirm && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" title="Conferma eliminazione">
              <Stack gap="sm">
                {isOnlyActiveColumn ? (
                  <Text size="sm">Non è possibile eliminare l'unica colonna attiva rimasta.</Text>
                ) : (
                  <>
                    <Text size="sm">
                      Sei sicuro di voler eliminare questa colonna? Questa azione non può essere annullata.
                    </Text>
                    {needsMoveTarget && (
                      <Select
                        label={`Sposta i ${column?.taskCount} task in…`}
                        placeholder="Seleziona una colonna di destinazione"
                        data={moveTargetOptions}
                        value={moveTasksToColumnId}
                        onChange={setMoveTasksToColumnId}
                        required
                      />
                    )}
                    <Group>
                      <Button color="red" size="xs" onClick={handleDelete} loading={isLoading} disabled={!canConfirmDelete}>
                        Conferma eliminazione
                      </Button>
                      <Button variant="subtle" size="xs" onClick={() => setShowDeleteConfirm(false)}>
                        Annulla
                      </Button>
                    </Group>
                  </>
                )}
              </Stack>
            </Alert>
          )}

          <Group justify="space-between" mt="md">
            <Group>
              {isEditMode && onDelete && !showDeleteConfirm && !isOnlyActiveColumn && (
                <Button color="red" variant="subtle" onClick={() => setShowDeleteConfirm(true)}>
                  Elimina
                </Button>
              )}
            </Group>

            <Group>
              <Button variant="subtle" onClick={handleClose}>
                Annulla
              </Button>
              <Button type="submit" loading={isLoading}>
                {isEditMode ? 'Salva modifiche' : 'Crea colonna'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
